import React from "react";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import KeyboardVoiceOutlinedIcon from "@mui/icons-material/KeyboardVoiceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import AudienceStoryPage from "../components/Landing/AudienceStoryPage";

const elderCareContent = {
  hero: {
    eyebrow: "Elder care",
    title: "Caring for a parent means tracking more than any one person can remember.",
    body: "Lifelog gives your whole family one place to capture what's happening, so nothing important is lost before the next appointment.",
    buttons: [
      { label: "I'm a family caregiver", href: "#family-caregivers" },
      { label: "I'm a care provider", href: "#care-provider" },
    ],
  },
  family: {
    eyebrow: "For family caregivers",
    title: "Everyone in your family sees something. Now it all goes in one place.",
    body: "Whether siblings are sharing shifts or family members are in different cities, Lifelog makes sure every observation reaches the people who need it.",
    cta: "Start tracking care together",
    features: [
      {
        title: "One timeline for the whole family",
        body: 'Siblings, spouses, paid helpers — everyone logs to the same place. No more "did anyone notice..." calls.',
        Icon: GroupsOutlinedIcon,
      },
      {
        title: "Capture what doctors need to know",
        body: "Medication reactions, falls, mood changes, sleep patterns. In your words, when they happen — not reconstructed later.",
        Icon: KeyboardVoiceOutlinedIcon,
      },
      {
        title: "Walk into appointments prepared",
        body: "Pull up months of observations in seconds. Stop trying to reconstruct the week from memory in the waiting room.",
        Icon: CalendarMonthOutlinedIcon,
      },
    ],
  },
  phone: {
    title: "Mom's log",
    dateRange: "June 2–8",
    entries: [
      ["Mon · 8:20 AM", "Confused this morning — didn't recognize the kitchen. Lasted ~30 min. Then totally fine.", "cognitive"],
      ["Tue · 2:15 PM", "Refused lunch again. Third day. Might be the new blood pressure med.", "appetite"],
      ["Wed · 10:30 AM", "Small fall in the bathroom — no injury. Holding onto sink helped. Called Dr. Chen.", "safety"],
      ["Fri · 4:00 PM", "Wonderful afternoon — recognized old friends in photos and told stories about them.", "good day"],
    ],
  },
  flow: {
    title: "How what happens at home reaches the care team",
    subtitle: "Family caregivers capture it. Lifelog organizes it. Doctors and care coordinators arrive informed.",
    steps: [
      {
        label: "Family",
        title: "Logs what they observe",
        body: "Any caregiver, anywhere — voice, text, or photo in under 10 seconds",
      },
      {
        label: "Lifelog",
        title: "Organizes everything",
        body: "All observations from all caregivers in one chronological timeline",
        highlight: true,
      },
      {
        label: "Care team",
        title: "Arrives prepared",
        body: "Doctor sees weeks of real observations — not just what the family remembers to mention",
      },
    ],
  },
  provider: {
    eyebrow: "For doctors & care coordinators",
    title: "What happens between visits matters.",
    body: "A fall at 2am. A week of appetite changes. A medication reaction no one thought to mention. Families witness it all — but rarely have a way to share it before they walk into your office.",
    summary: {
      label: "Appointment summary",
      title: "Margaret, age 78",
      date: "June 4, 2026",
      primaryHeading: "This week",
      primaryItems: [
        "Cognitive confusion Mon morning — ~30 min episode",
        "Appetite decline — 3 consecutive days, possible med reaction",
        "Bathroom fall Wed, no injury, held onto sink",
        "Strong recall and engagement on Friday afternoon",
      ],
      secondaryHeading: "Patterns to note",
      secondaryItems: [
        "Confusion episodes most frequent late morning",
        "Appetite issues began after medication change May 30",
      ],
      badge: "Generated from family logs",
      caption: "1 tap · ready before the appointment",
    },
    features: [
      {
        title: "Rich context before every visit",
        body: "See weeks of observations from everyone who cares for your patient — not just what they remember to tell you in 15 minutes.",
        Icon: AssignmentOutlinedIcon,
      },
      {
        title: "All caregivers, one record",
        body: "Multiple family members contributing means a more complete picture — no gaps from shift changes or missed handoffs.",
        Icon: GroupsOutlinedIcon,
      },
      {
        title: "Appointment summaries, one tap",
        body: "A structured summary generated from the family's logs, ready before they walk in. Focus on the patient, not on gathering information.",
        Icon: CheckBoxOutlinedIcon,
      },
    ],
    stepsEyebrow: "How it works with your practice",
    steps: [
      ["Recommend at intake", "Takes 2 minutes. Families set up before they leave your office."],
      ["Family logs at home", "Any caregiver captures observations in real time — falls, moods, medications, meals."],
      ["You get a shared view", "With permission, see weeks of observations from everyone involved in their care."],
      ["Visits become conversations", "No more starting from scratch each appointment. Real data, ready to go."],
    ],
    cta: "Recommend Lifelog to your patients' families",
  },
  cta: "Start tracking care together",
};

const familyCareContent = {
  ...elderCareContent,
  hero: {
    eyebrow: "Family caregiving",
    title: "Managing a chronic condition means tracking more than any one appointment can hold.",
    body: "Lifelog gives you and your care circle one place to capture what's really happening, so nothing important is lost before the next visit.",
    buttons: [
      { label: "I'm managing my own care", href: "#family-caregivers" },
      { label: "I'm a family caregiver", href: "#family-caregivers" },
    ],
  },
  family: {
    ...elderCareContent.family,
    eyebrow: "For you & your care circle",
    title: "Everything you notice, all in one place.",
    body: "Whether you're tracking your own symptoms or supporting someone you love, Lifelog makes sure every observation reaches the people who need it — in your own words, when it happens.",
    cta: "Start tracking your care story",
    features: [
      {
        title: "Log symptoms the moment they happen",
        body: "A pain flare at 2am. A fatigue spike mid-afternoon. Captured in seconds, in your own words — not reconstructed days later from memory.",
        Icon: KeyboardVoiceOutlinedIcon,
      },
      {
        title: "Your whole care circle stays in the loop",
        body: "Partners, parents, adult children, therapists — anyone you trust can contribute to a single shared record. No more fragmented updates or repeated explanations.",
        Icon: GroupsOutlinedIcon,
      },
      {
        title: "Walk into every appointment informed",
        body: "Weeks of real observations, ready in seconds. Stop reconstructing your health history from memory in the waiting room — and start having the conversations that matter.",
        Icon: CalendarMonthOutlinedIcon,
      },
    ],
  },
  phone: {
    title: "Jamie's log",
    dateRange: "June 2–8",
    entries: [
      ["Mon · 7:30 AM", "Joint stiffness on waking, both hands. Lasted about 2 hours before loosening up.", "pain"],
      ["Tue · 2:00 PM", "Bad fatigue spike after lunch — had to cancel PT. Took a long nap. Third time this month.", "fatigue"],
      ["Wed · 8:45 AM", "Plaquenil with food, no nausea today. Third morning in a row — seems to be helping.", "medication"],
      ["Fri · 10:00 AM", "Best day this week. Full morning of work, minimal pain, good energy. Went for a short walk.", "good day"],
    ],
  },
  flow: {
    title: "How what happens at home reaches your care team",
    subtitle: "You and your circle capture it. Lifelog organizes it. Your doctors and specialists arrive genuinely informed.",
    steps: [
      {
        label: "You & your circle",
        title: "Log what you observe",
        body: "Any caregiver, anywhere — voice, text, or quick note in under 10 seconds",
      },
      {
        label: "Lifelog",
        title: "Organizes everything",
        body: "All observations from all contributors in one chronological timeline",
        highlight: true,
      },
      {
        label: "Care team",
        title: "Arrives prepared",
        body: "Your specialist sees weeks of real observations — not just what you remember to mention",
      },
    ],
  },
  provider: {
    ...elderCareContent.provider,
    eyebrow: "For specialists & care teams",
    title: "What happens between visits matters.",
    body: "A flare at midnight. Three weeks of mounting fatigue. A medication that isn't working the way anyone expected. Your patients live with it daily — but rarely have a way to share it before they walk into your office.",
    summary: {
      label: "Appointment summary",
      title: "Jamie, age 34",
      date: "June 4, 2026",
      primaryHeading: "This week",
      primaryItems: [
        "Morning joint stiffness — both hands, ~2 hrs, worse than prior week",
        "Fatigue spike Tuesday, cancelled physical therapy session",
        "Plaquenil tolerated well — no nausea 3 consecutive mornings",
        "Strong energy and low pain reported Friday",
      ],
      secondaryHeading: "Patterns to note",
      secondaryItems: [
        "Morning stiffness severity increasing week over week",
        "Fatigue episodes most frequent mid-afternoon, especially post-activity",
      ],
      badge: "Generated from patient logs",
      caption: "1 tap · ready before the appointment",
    },
    features: [
      {
        title: "Rich context before every visit",
        body: "See weeks of real observations from patients and everyone in their care circle — not just what they remember to mention in 15 minutes.",
        Icon: AssignmentOutlinedIcon,
      },
      {
        title: "Multiple observers, one record",
        body: "When partners, parents, or other family members also contribute, you get a fuller picture — not just the patient's own recall, which fatigue and brain fog can affect.",
        Icon: GroupsOutlinedIcon,
      },
      {
        title: "Appointment summaries, one tap",
        body: "A structured summary from the patient's own logs, ready before they walk in. Focus the visit on the patient — not on gathering information from scratch.",
        Icon: CheckBoxOutlinedIcon,
      },
    ],
    steps: [
      ["Recommend at intake", "Takes 2 minutes. Patients are set up before they leave your office."],
      ["Patient & family log at home", "Symptoms, medication effects, mood changes — captured in real time, in their own words, by anyone in the care circle."],
      ["You get a shared view", "With permission, see weeks of observations from everyone involved — including family members who notice what the patient may not."],
      ["Visits become conversations", "No more starting from scratch each appointment. Real, timestamped data — ready to go."],
    ],
    cta: "Recommend Lifelog to your patients",
  },
  cta: "Start tracking your care story",
};

const CONTENT_BY_AUDIENCE = {
  elder: elderCareContent,
  family: familyCareContent,
};

const AudienceLandingPage = ({ audience = "family" }) => (
  <AudienceStoryPage content={CONTENT_BY_AUDIENCE[audience] || familyCareContent} />
);

export default AudienceLandingPage;
